import { skipToken, useSuspenseQuery } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  FacilityWasteView,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { useGeneratorStatusSummary } from "routes/Customer/Waste/Facilities/Facility/useGeneratorStatusSummary";
import { useWasteFacility } from "routes/Customer/Waste/Facilities/Facility/useWasteFacility";
import { prettyPrintMonthYear } from "util/dates";

/**
 * Window-bound calculated generator status: the server unifies vendor +
 * e‑Manifest generation across the requested window and returns the highest
 * calculated federal status. Bounded by the step's activity window (not the
 * calendar year) and based on all hazardous generation (`view: ALL`), since
 * generator status is a total-generation threshold independent of reportability.
 */
const CALCULATED_GENERATOR_STATUS = gql(`
  query WasteActivityCalculatedGeneratorStatus(
    $facilityId: ID!
    $filter: FacilityWasteShipmentsFilter!
  ) {
    facilityWasteShipmentCharts(facilityId: $facilityId, filter: $filter) {
      wasteShipmentsByMonth {
        highestCalculatedGeneratorStatus
      }
    }
  }
`);

function prettyPrintWasteGeneratorStatusLong(
  status: RcraInfoFederalWasteGeneratorStatus | StateRegulatoryStatus
): string {
  switch (status) {
    case RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator:
      return "Very Small Quantity Generator (VSQG)";
    case RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator:
      return "Small Quantity Generator (SQG)";
    case RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator:
      return "Large Quantity Generator (LQG)";
    case RcraInfoFederalWasteGeneratorStatus.NotAGenerator:
      return "Not a Generator";
    case RcraInfoFederalWasteGeneratorStatus.Undetermined:
      return "Undetermined";
    default:
      return prettyPrintEnumValue(status);
  }
}

/**
 * Simplified read-only generator status for the Waste Activity step: the EPA's
 * currently-reported status (with the EPA's as-of date), alongside Encamp's
 * single calculated status over the step's activity window. Deliberately omits
 * the dashboard tile's month-by-month exceedance/change breakdowns — the step
 * just needs to show "here's where the EPA has you, and here's what we calculate".
 *
 * Sourced facility-scoped (no `WasteFacilityContext`): facility `state` via
 * `useWasteFacility`, reported status via `useGeneratorStatusSummary`, and the
 * calculated status via `facilityWasteShipmentCharts` — all bounded to the
 * activity window.
 */
export function WasteGeneratorStatus({
  facilityId,
  startDate,
  endDate,
}: {
  facilityId: string;
  startDate: string;
  endDate: string;
}) {
  const { data: facilityData } = useWasteFacility(facilityId);
  const { data: summaryData } = useGeneratorStatusSummary(
    facilityId,
    startDate ? new Date(startDate) : null,
    endDate ? new Date(endDate) : null
  );
  const { data: chartsData } = useSuspenseQuery(
    CALCULATED_GENERATOR_STATUS,
    startDate && endDate
      ? {
          variables: {
            facilityId,
            filter: { startDate, endDate, view: FacilityWasteView.All },
          },
        }
      : skipToken
  );

  const facility = facilityData?.facility;
  const state = facility?.state ?? null;
  const reported = summaryData?.generatorStatusSummary?.currentlyReportedStatus;

  const calculated =
    chartsData?.facilityWasteShipmentCharts?.wasteShipmentsByMonth
      ?.highestCalculatedGeneratorStatus ?? null;

  // The status value renders on its own line beneath the field label (with an
  // optional "as of {date}" caption), matching the calculated-status field.
  const renderStatusValue = (
    status: RcraInfoFederalWasteGeneratorStatus | StateRegulatoryStatus | null | undefined,
    date: string | null | undefined
  ) => (
    <Stack spacing={0}>
      <Typography variant="body2">
        {status ? prettyPrintWasteGeneratorStatusLong(status) : "—"}
      </Typography>
      {date && (
        <Typography variant="caption" color="text.secondary">
          as of {prettyPrintMonthYear(date)}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          Federal
        </Typography>
        <DisplayOnlyField
          label="EPA reported status"
          renderValue={() =>
            renderStatusValue(
              reported?.fedWasteGenerator?.status,
              reported?.fedWasteGenerator?.createdDate
            )
          }
        />
        <DisplayOnlyField
          label="Encamp calculated status"
          text={calculated ? prettyPrintWasteGeneratorStatusLong(calculated) : "Undetermined"}
        />
      </Stack>
      {state && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            State
          </Typography>
          <DisplayOnlyField
            label="EPA reported status"
            renderValue={() =>
              renderStatusValue(
                reported?.stateWasteGenerator?.status,
                reported?.stateWasteGenerator?.createdDate
              )
            }
          />
        </Stack>
      )}
    </Stack>
  );
}
